import { encodeBase64 } from "@p0security/shared/base64";
import { AppPaths } from "@p0security/shared/routes/constants";
import {
  ALL_SCOPE_SENTINEL,
  toScope,
} from "@p0security/shared/types/assessment";
import { AnyNode } from "@p0security/shared/types/assessment/data/index";
import { FindingState } from "@p0security/shared/types/assessment/finding";
import { Collapse, Typography } from "antd";
import { CommandDisplay } from "components/Integrations/CommandDisplay";
import {
  DEFAULT_TAB_SEARCH_PARAM,
  YAML_RULE_SEARCH_PARAM,
} from "components/Jit/Routing/constants";
import { TenantAwareLink } from "components/common/TenantAwareLink";
import { useContext } from "react";

import { MonitorWithFindings } from "../contexts/FindingsContext";
import { ScopeContext } from "../contexts/ScopeContext";

export const FindingsFixOrRevertDemo: React.FC<{
  nodes: AnyNode[];
  monitor: MonitorWithFindings;
  findingState: FindingState;
}> = ({ monitor, nodes, findingState }) => {
  const { integration, scopeKey } = useContext(ScopeContext);
  const fix =
    findingState === "open"
      ? monitor?.fix?.[integration]
      : monitor?.revert?.[integration];
  const scope = toScope(scopeKey);

  if (!fix || !integration || scope === ALL_SCOPE_SENTINEL) return null;

  const generateRoutingRule = fix.generateRoutingRule;
  return (
    <div>
      <Typography.Title level={3}>{monitor.label}</Typography.Title>
      <Collapse>
        <Collapse.Panel key="remediate" header="Remediate">
          <Typography.Paragraph>{fix.description}</Typography.Paragraph>
          <CommandDisplay
            commands={nodes
              .map(
                (n) =>
                  fix.generate(n, { scope })?.trim() ??
                  `# Finding with key ${n.key} does not have a fix available`
              )
              .join("\n\n")}
            maxRows={25}
          />
        </Collapse.Panel>
        {generateRoutingRule && (
          <Collapse.Panel key="jit" header="Replace with just-in-time access">
            <Typography.Paragraph>
              {
                "To replace this access with just-in-time access, add the policy below to your just-in-time routing configuration."
              }
            </Typography.Paragraph>
            {nodes.length === 1 &&
              (() => {
                const yamlStr = generateRoutingRule(nodes[0], { scope });
                if (!yamlStr) return null;
                const encodedYaml = encodeURIComponent(
                  encodeBase64(yamlStr.trim())
                );
                const searchParams = new URLSearchParams();
                searchParams.set(YAML_RULE_SEARCH_PARAM, encodedYaml);
                searchParams.set(DEFAULT_TAB_SEARCH_PARAM, "approval");

                return (
                  <Typography.Paragraph>
                    {" "}
                    Alternatively,{" "}
                    <TenantAwareLink
                      to={`${AppPaths.Policies}?${searchParams.toString()}`}
                    >
                      open in Policies
                    </TenantAwareLink>{" "}
                    to interactively create the routing rule.
                  </Typography.Paragraph>
                );
              })()}
            <CommandDisplay
              commands={nodes
                .map(
                  (n) =>
                    generateRoutingRule(n, { scope })?.trim() ??
                    `# Finding with key ${n.key} does not have a routing rule available`
                )
                .join("\n\n")}
              maxRows={25}
            />
          </Collapse.Panel>
        )}
      </Collapse>
    </div>
  );
};
